import { UPLOAD_FILE_ENDPOINT } from "../../../core/const/Constant";
import { uploadFile } from "../../api/upload.api";
import { BASE64_ICON } from "../../const/icons";

import FileUtil from "../../utils/File.util";
import Utils from "../../utils/Common.util";
import TaiBaseWidget from "../base.widget";
import Dropzone from 'dropzone';

import fileUploadTemplate from './FileUpload.template.html';
import previewFileTemplate from './PreviewFile.template.html';
import { useReactiveZone } from "reactive-zone/src/index.js";


/**
 * @typedef {{
 *  filename:string,
 *  message: string,
 *  status_form: "upload_file_success" | "upload_file_fail",
 *  dzFile: object
 * }} FileUploadedType
 */


/**
 * 
 * @param {string} strExts 
 * @returns {string}
 */
const rewriteUploadExtension = (strExts) => strExts.split(',').map(ext => {
    const trimExt = ext.trim();
    return trimExt.startsWith('.') ? trimExt : ('.' + trimExt);
}).join(',');


const generateFileId = (() => {
    let fileCount = 1;

    return () => {
        fileCount++;
        return new Date().getTime() + "_" + fileCount;
    }
})();

class FileUploadWidget extends TaiBaseWidget {

    /** @type Map<object,FileUploadedType> */
    fileTemporariesUploaded = new Map();
    dropzoneIntance;
    deletedFileIds = new Set()

    zone = useReactiveZone(this)
    refDropzone = this.zone.useElementRef('ref.dropzone');

    template() {
        return fileUploadTemplate;
    }

    onRendered() {
        const uploadUrl = UPLOAD_FILE_ENDPOINT + `?uuid=${this.fieldModel.formUUID}`;
        if (this.fieldModel.storeTemp) {
            this.fileTemporariesUploaded = this.fieldModel.storeTemp
        } else {
            this.fieldModel.storeTemp = this.fileTemporariesUploaded;
        }

        const { upload_file_min,
            upload_file_max,
            upload_file_mime_type,
            upload_file_size
        } = this.fieldModel.extends || {
            upload_file_min: 1,
            upload_file_max: 1,
            upload_file_mime_type: "*",
            upload_file_size: 5 * 1024 * 1024
        }
        const fileSizeByMb = Number((upload_file_size / 1024 / 1024).toFixed(2));
        this.updateFileCounter();

        // const fileBigMessage = this.fieldModel.validations.find(i => i.rule === 'uploadFileSize').message;

        this.dropzoneIntance = new Dropzone(this.refDropzone.element, {
            url: uploadUrl,
            autoProcessQueue: false,
            clickable: true,
            acceptedFiles: rewriteUploadExtension(upload_file_mime_type),
            maxFiles: upload_file_max,
            maxFilesize: fileSizeByMb,
            // dictFileTooBig: fileBigMessage,
            previewsContainer: this.querySelector(".tai-files-preview"),
            previewTemplate: previewFileTemplate,
            thumbnailWidth: null,
            thumbnailHeight: null,
            thumbnailMethod: "contain",
            parallelUploads: 1,
            hiddenInputContainer: this.selfRoot,
            init: () => {
                Utils.Delay(10).then(() => {
                    if (this.fileTemporariesUploaded.size > 0) {
                        for (const item of this.fileTemporariesUploaded.values()) {
                            this.dropzoneIntance.displayExistingFile(item.dzFile, item.dzFile.dataURL)
                            this.dropzoneIntance.files.push(item.dzFile);
                        }
                    }
                });
            }

        });
        this.dropzoneIntance.on('addedfile', (/** @{ IFile} */file) => {


            const { previewTemplate, name, dataURL } = file;
            file.fileId = generateFileId();
            if (!previewTemplate) return;
            const ext = name.split('.').pop().toLowerCase();
            const thumbElem = previewTemplate.querySelector("[data-dz-thumbnail]");
            if (BASE64_ICON[ext]) {
                thumbElem.src = BASE64_ICON[ext];
                file.dataURL = BASE64_ICON[ext]
            } else {
                thumbElem.src = dataURL;
            }

            const fileToLarge = file.size > Number(upload_file_size);
            if (fileToLarge) {
                const lbError = previewTemplate.querySelector("[data-dz-thumbnail]");
                lbError.innerHTML = 'File Large';
            }

            const fileExisted = this.checkFileExisted(file);
            if (!fileExisted && !fileToLarge) {
                this.doUpload(file)
            }
        })

        this.dropzoneIntance.on('success', this.onFileTemporaryUploaded.bind(this));
        this.dropzoneIntance.on("removedfile", this.onFileRemoved.bind(this));

    }

    /**
     * 
     * @param { IFile & {previewElement:HTMLElement}} file 
     */
    doUpload(file) {
        const { previewElement } = file;

        previewElement.querySelector(".tai-files-preview__thumb").insertAdjacentHTML("beforeend", `<div class="tai-loader"><div></div></div>`);
        FileUtil.FileToArrayBuffer(file).then(fileData => {
            const { size, name, type, fileId } = file;
            return uploadFile(this.fieldModel.formUUID, { size, name, type, fileData, fileId }, this.fieldModel.code)
        }).then(({ responseForm, errorMessages }) => {
            const { status_form } = responseForm;
            if (status_form === 'upload_file_success') {
                return Promise.resolve(responseForm)
            } else if (status_form === 'upload_file_fail') {
                const { file: errorMessage } = errorMessages;
                return Promise.reject(errorMessage);
            }
        }).then((responseForm) => {
            // Store Temp
            this.onFileTemporaryUploaded(file, responseForm)
            previewElement.classList.add("upload-completed");
            Utils.Delay(1000).then(() => previewElement.classList.add("upload-completed"))
        }).catch((errorMessage) => {
            previewElement.classList.add("upload-failed");
            previewElement.querySelector('[data-dz-errormessage]').innerHTML = errorMessage;
        }).finally(() => {
            // Remove loader
            const loader = previewElement.querySelector('.tai-loader');
            loader.parentElement.removeChild(loader);
        })
    }

    updateFileCounter() {
        const { upload_file_max } = this.fieldModel.extends;
        this.querySelector(".tai-uploadzone__counter").innerText = `${this.fileTemporariesUploaded.size} of ${upload_file_max}`;
    }

    onFileChanges() {
        this.updateFileCounter();
        const { upload_file_max } = this.fieldModel.extends;
        const countFile = this.fileTemporariesUploaded.size;
        this.fieldModel.userResponse = this.getValue();
        this.doValidate();
        if (countFile >= upload_file_max) {
            this.querySelector('.dropzone').classList.add('dz-max-files-reached');
        } else {
            this.querySelector('.dropzone').classList.remove('dz-max-files-reached');
        }
    }

    /**
     * 
     * @param {IFile} file - DZFile 
     */
    checkFileExisted(file) {
        return this.fileTemporariesUploaded.has(file);
    }

    /**
     * 
     * @param {IFile} file 
     */
    onFileRemoved(file) {
        this.fileTemporariesUploaded.delete(file);
        this.deletedFileIds.add(file.fileId);
        this.onFileChanges();
    }

    /**
     * @param {IFile} file 
     * @param {FileUploadedType} responseForm
     */
    onFileTemporaryUploaded(file, responseForm) {
        if (this.deletedFileIds.has(file.fileId)) {
            return;
        }
        responseForm.dzFile = file;
        this.fileTemporariesUploaded.set(file, responseForm)
        this.onFileChanges();
    }

    setValue() {

    }

    getValue() {
        const files = Array.from(this.fileTemporariesUploaded.values()).map((item) => item.filename);
        return files.length === 0 ? '' : files;
    }

    disableAutoDetectInputChanges() {
        return true;
    }

    onValidateRule(rule, filesName, format) {
        if (rule === "uploadFileSize") {
            // const files = Array.from(this.fileTemporariesUploaded.values()).find(item => item.filename === value);
            // for (let i in file) {
            //     const file = [file]
            // }
            // return file && file.dzFile.size > Number(format);
            return true;
        }
        return super.onValidateRule(...arguments);
    }
}

export default FileUploadWidget;


